/**
 * t622u: scheduled chat messages ("write now, send later"). The quick choices in the composer's Schedule menu, the
 * Custom… date + time, and how a send time reads, all in Europe/London (BST/GMT handled by Luxon's zone rules), so the
 * web app, the native app and the API agree on what "Tomorrow at 9:00" means.
 */
import { DateTime } from 'luxon';
import { formatGB } from './gbDate';
import type { AttachmentSummary, ConversationKind } from './types';

export const SCHEDULE_ZONE = 'Europe/London';
/** The furthest ahead a message can be scheduled. */
export const SCHEDULE_MAX_DAYS = 366;
/** A quick choice is only offered when it is at least this far ahead (e.g. "Later today at 17:00" at 16:59 isn't). */
export const SCHEDULE_MIN_LEAD_MS = 60_000;

export type ScheduledMessageStatus = 'scheduled' | 'sent' | 'cancelled' | 'failed';

export interface ScheduledMessage {
  id: string;
  workspace_id: string;
  conversation_id: string;
  parent_id: string | null;
  body: string;
  mentions: string[];
  attachment_ids: string[];
  attachments: AttachmentSummary[];
  send_at: string;
  status: ScheduledMessageStatus;
  failure_reason: string | null;
  message_id: string | null;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  /** Where it goes, for the Scheduled messages list ("#general", "Sarah Jones", "Sarah, Tom"). */
  conversation: { kind: ConversationKind; label: string };
}

export interface ScheduleMessageInput {
  body: string;
  parent_id?: string | null;
  mentions?: string[];
  attachment_ids?: string[];
  send_at: string;
}

export interface UpdateScheduledMessageInput {
  body?: string;
  mentions?: string[];
  send_at?: string;
}

export interface ScheduleChoice { id: 'this_morning' | 'later_today' | 'tomorrow' | 'monday'; label: string; at: string }
/** At most this many quick choices (then Custom…). */
export const SCHEDULE_MAX_CHOICES = 3;

const london = (now: Date | DateTime) => (now instanceof Date ? DateTime.fromJSDate(now) : now).setZone(SCHEDULE_ZONE);
/** Wall-clock time on a London calendar day (Luxon resolves the offset for that day, so BST/GMT is right either side of a clock change). */
const at = (day: DateTime, hour: number) => day.set({ hour, minute: 0, second: 0, millisecond: 0 });

/**
 * The composer's quick choices (Europe/London), most likely first, at most three, never a time that has passed (or
 * is under a minute away) and never the same time twice:
 *  1. "This morning at 9:00" before about 8:59 (t656u: after midnight, the likeliest choice);
 *  2. "Later today at 17:00" before about 16:59;
 *  3. "Tomorrow at 9:00" (always, so it's also Custom…'s starting point);
 *  4. "Monday at 9:00" the coming Monday, left out when today or tomorrow is Monday.
 */
export function scheduleChoices(now: Date | DateTime = new Date()): ScheduleChoice[] {
  const n = london(now);
  const today = n.startOf('day'), tomorrow = today.plus({ days: 1 });
  const ahead = (t: DateTime) => t.toMillis() - n.toMillis() >= SCHEDULE_MIN_LEAD_MS;
  const all: ScheduleChoice[] = [];
  const add = (id: ScheduleChoice['id'], label: string, t: DateTime) => { if (ahead(t)) all.push({ id, label, at: t.toUTC().toISO()! }); };
  add('this_morning', 'This morning at 9:00', at(today, 9));
  add('later_today', 'Later today at 17:00', at(today, 17));
  add('tomorrow', 'Tomorrow at 9:00', at(tomorrow, 9));
  if (today.weekday !== 1 && tomorrow.weekday !== 1) add('monday', 'Monday at 9:00', at(today.plus({ days: 8 - today.weekday }), 9));
  // At most two come before Tomorrow, so it is always kept; Monday is the one that drops before 9:00.
  return all.slice(0, SCHEDULE_MAX_CHOICES);
}

export type LondonTimeResult = { ok: true; at: string } | { ok: false; error: string };

/**
 * Custom…: a London date (yyyy-MM-dd) and time (HH:mm) as an instant. Refuses a time that doesn't exist (the hour the
 * clocks go forward in March), one that has passed or is too far ahead. In the repeated hour in October the first
 * (BST) one is used.
 */
export function londonDateTime(date: string, time: string, now: Date | DateTime = new Date()): LondonTimeResult {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{1,2}:\d{2}$/.test(time)) return { ok: false, error: 'Pick a date and time' };
  const [h, m] = time.split(':').map(Number) as [number, number];
  const d = DateTime.fromISO(date, { zone: SCHEDULE_ZONE }).set({ hour: h, minute: m, second: 0, millisecond: 0 });
  if (!d.isValid || h > 23 || m > 59) return { ok: false, error: 'Pick a date and time' };
  // Luxon moves a wall-clock time inside the spring-forward gap on by an hour; that time doesn't exist that day.
  if (d.hour !== h || d.minute !== m) return { ok: false, error: 'That time doesn’t exist on this day (the clocks go forward). Pick another time.' };
  return checkSendAt(d.toUTC().toISO()!, now);
}

/** The server's (and the pickers') rule: in the future, and within SCHEDULE_MAX_DAYS. */
export function checkSendAt(iso: string, now: Date | DateTime = new Date()): LondonTimeResult {
  const d = DateTime.fromISO(iso, { zone: 'utc' });
  if (!d.isValid) return { ok: false, error: 'Pick a date and time' };
  const n = now instanceof Date ? DateTime.fromJSDate(now) : now;
  if (d.toMillis() <= n.toMillis()) return { ok: false, error: 'Pick a time in the future' };
  if (d.toMillis() - n.toMillis() > SCHEDULE_MAX_DAYS * 86_400_000) return { ok: false, error: 'Pick a time within the next year' };
  return { ok: true, at: d.toISO()! };
}

/**
 * How a send time reads: "today at 17:00", "tomorrow at 9:00", "Mon 9 Nov at 9:00" (another year: "Mon 4 Jan 2027 at 9:00").
 * Lower case first so it fits "Sends tomorrow at 9:00"; capitalise for a heading with `sendTimeLabel(...)[0].toUpperCase()`.
 */
export function sendTimeLabel(iso: string, now: Date | DateTime = new Date()): string {
  const d = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SCHEDULE_ZONE);
  const n = london(now);
  const time = formatGB(d, 'H:mm');
  const days = Math.round(d.startOf('day').diff(n.startOf('day'), 'days').days);
  if (days === 0) return `today at ${time}`;
  if (days === 1) return `tomorrow at ${time}`;
  return `${formatGB(d, d.year === n.year ? 'ccc d LLL' : 'ccc d LLL yyyy')} at ${time}`;
}

/** The London date and time inputs' values for an instant (Edit / Custom… start from these). */
export function londonInputs(iso: string): { date: string; time: string } {
  const d = DateTime.fromISO(iso, { zone: 'utc' }).setZone(SCHEDULE_ZONE);
  return { date: formatGB(d, 'yyyy-MM-dd'), time: formatGB(d, 'HH:mm') };
}

/** "1 scheduled message" / "3 scheduled messages" (the line above the composer). */
export const scheduledCountLabel = (n: number) => `${n} scheduled message${n === 1 ? '' : 's'}`;
